import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBallots } from '../../contexts/BallotsContext';
import { useCategories } from '../../contexts/CategoriesContext';
import { useSettings } from '../../contexts/SettingsContext';
import { getCategoryWinnerIds, getCategoryWinnersText } from '../../utils/categoryWinners';
import { hasLeaderboardAccess } from '../../utils/leaderboardAccess';
import { LEADERBOARD_ACCESS_CODE } from '../../config/constants';
import './PublicLeaderboardPage.css';

const PublicLeaderboardPage = () => {
  const navigate = useNavigate();
  const { ballots, loading } = useBallots();
  const { categories, loading: categoriesLoading } = useCategories();
  const { settings } = useSettings();
  const hasAccess = hasLeaderboardAccess(LEADERBOARD_ACCESS_CODE);

  useEffect(() => {
    if (!hasAccess) {
      navigate('/', { replace: true });
    }
  }, [hasAccess, navigate]);

  // Check if party has started
  const partyStartDateValue = settings?.partyStartDate || settings?.partyDate;
  const partyDate = partyStartDateValue ? new Date(partyStartDateValue) : null;
  const leaderboardYear = settings?.year || (partyDate && !Number.isNaN(partyDate.getTime())
    ? partyDate.getFullYear()
    : new Date().getFullYear());

  // Sort categories by order
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      return orderA - orderB;
    });
  }, [categories]);

  // Process ballots with category scores
  const leaderboardData = useMemo(() => {
    return ballots.map(ballot => {
      // Create a map of category selections for this ballot
      const selectionsMap = new Map();
      if (ballot.userCategoriesSelections) {
        ballot.userCategoriesSelections.forEach(sel => {
          selectionsMap.set(sel.categoryId, sel.selection?.nomineeId);
        });
      }

      // Calculate score for each category
      const categoryScores = {};
      sortedCategories.forEach(category => {
        const categoryId = category.categoryId || category.id;
        const winnerIds = getCategoryWinnerIds(category);
        const userSelection = selectionsMap.get(categoryId);
        const categoryPoints = category.points || 1;

        if (winnerIds.size === 0) {
          // No winner announced yet - blank
          categoryScores[categoryId] = null;
        } else if (winnerIds.has(userSelection)) {
          // Correct prediction - show points
          categoryScores[categoryId] = categoryPoints;
        } else {
          // Wrong prediction - show 0
          categoryScores[categoryId] = 0;
        }
      });

      return {
        id: ballot.id,
        name: ballot.userDisplayName || ballot.userName || 'Unknown',
        totalScore: ballot.totalScore || 0,
        categoryScores
      };
    });
  }, [ballots, sortedCategories]);

  // Sort by total score descending and calculate ranks with ties
  const sortedLeaderboard = useMemo(() => {
    const sorted = [...leaderboardData].sort((a, b) => {
      if (b.totalScore !== a.totalScore) {
        return b.totalScore - a.totalScore;
      }
      return (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' });
    });

    // Calculate ranks with tie handling
    let currentRank = 1;
    return sorted.map((player, index) => {
      // If not the first player and score is different from previous, update rank
      if (index > 0 && player.totalScore < sorted[index - 1].totalScore) {
        currentRank = index + 1; // Rank jumps to position (accounts for ties)
      }
      return {
        ...player,
        rank: currentRank
      };
    });
  }, [leaderboardData]);

  const getCategoryHeaderLabel = (label) => {
    const value = (label || '').trim();
    const parts = value.split(/\s+/);
    if (parts.length === 2) {
      return `${parts[0]}\n${parts[1]}`;
    }
    return value;
  };

  const getCategoryLabelText = (category) => (
    category.abbreviatedName || category.name || ''
  ).trim();

  const isScreenplayCategory = (category) => {
    const text = getCategoryLabelText(category).toLowerCase();
    return text.includes('screenplay');
  };

  const isInternationalCategory = (category) => {
    const text = getCategoryLabelText(category).toLowerCase();
    return text.includes('international');
  };

  const getCategoryColumnClassName = (category) => {
    const classNames = [];
    if (isScreenplayCategory(category)) classNames.push('category-col--screenplay');
    if (isInternationalCategory(category)) classNames.push('category-col--international');
    return classNames.join(' ');
  };

  if (!hasAccess) {
    return null;
  }

  if (loading || categoriesLoading) {
    return (
      <div className="public-leaderboard-page">
        <div className="leaderboard-container">
          <div className="past-winners-loading">
            <div className="loading-spinner"></div>
            <p>Loading leaderboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-leaderboard-page">
      <div className="leaderboard-container">
        <header className="leaderboard-header">
          <h1 className="leaderboard-title">
            <span className="leaderboard-title__glow">MR Oscar Party {leaderboardYear} Leaderboard</span>
          </h1>
          <p className="leaderboard-subtitle"><em>You Must Be Present to Win!!</em></p>
        </header>

        <section className="leaderboard-shell">
          <div className="leaderboard-shell__inner">
            {sortedLeaderboard.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h2>No scores yet</h2>
                <p>The leaderboard will appear here once ballots are submitted and scored.</p>
              </div>
            ) : (
              <div className="leaderboard-table-wrapper">
                <table className="leaderboard-table leaderboard-table-detailed">
                  <thead>
                    <tr className="header-row-titles">
                      <th className="th-rank">Rank</th>
                      <th className="th-name">Name</th>
                      <th className="th-score">Score</th>
                      {sortedCategories.map(category => (
                        <th
                          key={category.categoryId || category.id}
                          className={`th-category ${getCategoryColumnClassName(category)}`.trim()}
                        >
                          <span
                            className={`category-header-text ${isInternationalCategory(category) ? 'category-header-text--single-line' : ''}`.trim()}
                            title={category.abbreviatedName}
                          >
                            {getCategoryHeaderLabel(category.abbreviatedName)}
                          </span>
                        </th>
                      ))}
                    </tr>
                    <tr className="header-row-winners">
                      <th className="th-winner-label" colSpan={3}>Winners</th>
                      {sortedCategories.map(category => {
                        const winnerText = getCategoryWinnersText(category);
                        return (
                          <th
                            key={category.categoryId || category.id}
                            className={`th-winner ${getCategoryColumnClassName(category)}`.trim()}
                          >
                            <span className="winner-text" title={winnerText}>
                              {winnerText || '—'}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeaderboard.map((player, index) => (
                      <tr key={player.id || index}>
                        <td className="rank-cell">
                          <span className="rank-number">{player.rank}.</span>
                        </td>
                        <td className="name-cell">
                          <span className="player-name">{player.name}</span>
                        </td>
                        <td className="score-cell">
                          <span className="score-value">{player.totalScore}</span>
                        </td>
                        {sortedCategories.map(category => {
                          const categoryId = category.categoryId || category.id;
                          const score = player.categoryScores[categoryId];

                          return (
                            <td
                              key={categoryId}
                              className={`category-score-cell ${score === null ? 'pending' : score > 0 ? 'correct' : 'incorrect'} ${getCategoryColumnClassName(category)}`.trim()}
                            >
                              {score === null ? '' : score}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PublicLeaderboardPage;
